import { useState, useRef, useEffect, useCallback } from 'react';
import { useCompare } from '../../../context/CompareContext';
import { useCart } from '../../../context/CartContext';

const WHATSAPP = '18492081166';
const DISMISS_KEY = 'spartech_bot_dismissed_until';
const DISMISS_MINUTES = 10;

interface Message { role: 'bot' | 'user'; text: string; }

const KB = [
  { keys: ['precio','costo','cuánto','cuanto','vale','económico','barato','caro'], answer: 'Nuestros precios varían por producto: GPUs desde RD$15,000, CPUs desde RD$8,000, RAM desde RD$3,500 y ensamblajes completos desde RD$45,000. Visita el catálogo para precios actualizados.' },
  { keys: ['horario','hora','abierto','cuando','cierra','abre'], answer: 'Estamos abiertos de lunes a sábado de 9:00 AM a 6:00 PM. Domingos y feriados permanecemos cerrados.' },
  { keys: ['dirección','ubicación','donde','lugar','tienda','local','mapa'], answer: 'Estamos en C/Octavio Mejía Ricart Esq. C/Aruba #61, Ensanche Ozama, Santo Domingo Este, RD.' },
  { keys: ['garantía','garantia','warranty','devolver','cambio','devolución'], answer: 'Todos los productos tienen 3 meses de garantía SpartechRD y los ensamblajes 1 año. La garantía del fabricante es aparte y se gestiona directamente con la marca según sus políticas.' },
  { keys: ['envío','envio','delivery','despacho','entrega','llegar'], answer: 'Realizamos envíos a toda la República Dominicana. Entrega estándar 2-5 días, express 1-2 días. Gratis en pedidos +RD$5,000 en el Gran Santo Domingo.' },
  { keys: ['armar','ensamblaje','arma','configurar','construir','build','pc','computadora'], answer: 'Ensamblamos PCs a medida. Puedes usar nuestro configurador en /arma-tu-pc para elegir componentes y cotizar. También aceptamos componentes propios.' },
  { keys: ['servicio','reparar','reparación','mantenimiento','técnico'], answer: 'Ofrecemos reparación, mantenimiento preventivo, limpieza profunda y soporte remoto. Puedes agendar una cita en /agendar o llamarnos al (849) 208-1166.' },
  { keys: ['cita','agendar','agenda','reservar','turno','appointment'], answer: 'Puedes agendar tu cita en la sección /agendar del sitio. Elige fecha, hora y llena el formulario. Un técnico certificado te atenderá.' },
  { keys: ['whatsapp','chat','contactar','contacto','llamar','teléfono','telefono'], answer: 'Puedes contactarnos por WhatsApp al (849) 208-1166 o por teléfono al (829) 596-1615. También tienes el formulario de contacto en la página.' },
  { keys: ['gpu','tarjeta gráfica','grafica','rtx','radeon','nvidia','amd'], answer: 'Tenemos GPUs NVIDIA RTX 4060, 4070, 4080 y AMD RX 7600, 7700. Precios desde RD$15,000. Visita el catálogo para disponibilidad actual.' },
  { keys: ['cpu','procesador','intel','ryzen','i5','i7','i9'], answer: 'Manejamos procesadores Intel Core i5/i7/i9 de última generación y AMD Ryzen 5/7/9. Desde RD$8,000. Revisa el catálogo para stock.' },
  { keys: ['ram','memoria','ddr4','ddr5'], answer: 'Tenemos kits de RAM DDR4 y DDR5 de 8GB, 16GB, 32GB y 64GB. Marcas como Corsair, Kingston y G.Skill. Desde RD$3,500.' },
  { keys: ['pagos','cuotas','financiamiento','credito','tarjeta'], answer: 'Aceptamos pagos en efectivo, transferencia bancaria y tarjetas de crédito.' },
  { keys: ['empresa','corporativo','gobierno','institución','licitación'], answer: 'Atendemos empresas e instituciones públicas. Emitimos comprobante fiscal (NCF), ofrecemos precios por volumen y planes de mantenimiento corporativo.' },
  { keys: ['seguimiento','ticket','estado','pedido'], answer: 'Puedes ver el estado de tu equipo en la sección /seguimiento ingresando tu número de ticket (formato SPT-XXXXX).' },
  { keys: ['catalogo','catálogo','productos','inventario','stock'], answer: 'Nuestro catálogo está disponible en /products con más de 1,200 productos. Puedes filtrar por categoría y ver disponibilidad en tiempo real.' },
  { keys: ['monitor','pantalla','display','hz','144hz','1080p','4k'], answer: 'Manejamos monitores gaming desde 24" hasta 32", resoluciones FHD a 4K, con tasas de refresco de 60Hz a 240Hz. Desde RD$14,000.' },
  { keys: ['hola','buenas','hey','saludos','buenos días','buenas tardes'], answer: '¡Hola! Soy el asistente virtual de SpartechRD 🔴 ¿En qué puedo ayudarte hoy? Puedo responder preguntas sobre productos, precios, servicios, horarios y más.' },
  { keys: ['gracias','thank','perfecto','excelente'], answer: '¡Con gusto! Si tienes otra pregunta, estoy aquí. También puedes chatear con un agente humano por WhatsApp si necesitas ayuda personalizada.' },
];

function findAnswer(input: string): string | null {
  const q = input.toLowerCase();
  for (const entry of KB) {
    if (entry.keys.some((k) => q.includes(k))) return entry.answer;
  }
  return null;
}

const WELCOME: Message = {
  role: 'bot',
  text: '¡Hola! Soy el asistente de SpartechRD ⚡ Pregúntame sobre precios, productos, servicios, horarios o cómo llegar.',
};

function isDismissed(): boolean {
  const until = localStorage.getItem(DISMISS_KEY);
  if (!until) return false;
  return Date.now() < Number(until);
}

function dismissFor(minutes: number) {
  localStorage.setItem(DISMISS_KEY, String(Date.now() + minutes * 60 * 1000));
}

export default function GamingMascot() {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(isDismissed);
  const [messages, setMessages] = useState<Message[]>([WELCOME]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [hover, setHover] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const revealTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { compareModalOpen, compareItems } = useCompare();
  const { drawerOpen } = useCart();

  // Auto-scroll chat
  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  // Re-check dismiss timer periodically
  useEffect(() => {
    if (!hidden) return;
    const check = setInterval(() => {
      if (!isDismissed()) {
        setHidden(false);
        clearInterval(check);
      }
    }, 30_000);
    return () => clearInterval(check);
  }, [hidden]);

  const handleDismiss = useCallback(() => {
    dismissFor(DISMISS_MINUTES);
    setOpen(false);
    setHidden(true);
    // Schedule re-appearance
    if (revealTimer.current) clearTimeout(revealTimer.current);
    revealTimer.current = setTimeout(() => {
      setHidden(false);
    }, DISMISS_MINUTES * 60 * 1000);
  }, []);

  useEffect(() => () => {
    if (revealTimer.current) clearTimeout(revealTimer.current);
  }, []);

  const sendMessage = async (text?: string) => {
    const q = (text ?? input).trim();
    if (!q) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: q }]);
    setThinking(true);
    await new Promise((r) => setTimeout(r, 600 + Math.random() * 400));
    const answer = findAnswer(q);
    setMessages((m) => [
      ...m,
      {
        role: 'bot',
        text: answer ?? '¡Buena pregunta! Para darte la mejor respuesta, te recomiendo hablar directamente con uno de nuestros agentes. Disponibles Lun-Sáb 9AM-6PM.',
      },
    ]);
    setThinking(false);
  };

  const openWhatsApp = () =>
    window.open(`https://wa.me/${WHATSAPP}?text=Hola SpartechRD, necesito ayuda.`, '_blank');

  // Hide bot completely when compare bar is shown, compare modal is open, or cart is open
  const shouldHide = hidden || compareModalOpen || drawerOpen || compareItems.length > 0;
  if (shouldHide) return null;

  return (
    <div
      className="fixed bottom-16 sm:bottom-6 right-3 sm:right-6 z-[100] flex flex-col items-end gap-3"
    >
      <style>{`
        @keyframes mascotFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        @keyframes eyeBlink { 0%,85%,100%{transform:scaleY(1)} 90%{transform:scaleY(0.08)} }
        @keyframes antPulse { 0%,100%{box-shadow:0 0 4px 2px rgba(228,27,29,.6)} 50%{box-shadow:0 0 10px 5px rgba(228,27,29,1)} }
        @keyframes chatIn { from{opacity:0;transform:translateY(10px) scale(.95)} to{opacity:1;transform:none} }
        .mf{animation:mascotFloat 4s ease-in-out infinite;will-change:transform;}
        .eb{animation:eyeBlink 4s ease-in-out infinite;display:block}
        .ap{animation:antPulse 1.5s ease-in-out infinite}
        .ci{animation:chatIn .2s ease-out forwards}
      `}</style>

      {/* Chat panel */}
      {open && (
        <div
          className="ci bg-zinc-950 border border-zinc-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          style={{ width: 'min(320px, calc(100vw - 2rem))', height: '420px' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-zinc-900 border-b border-zinc-800 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-zinc-950 border border-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-robot-line text-red-400 text-sm" />
              </div>
              <div>
                <p className="text-white text-xs font-bold">SpartechRD AI</p>
                <p className="text-emerald-700 text-[9px] flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse inline-block" />
                  En línea
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={openWhatsApp}
                title="Hablar con agente"
                className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-500/15 hover:bg-emerald-500/30 transition-colors cursor-pointer"
              >
                <i className="ri-whatsapp-line text-emerald-700 text-sm" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="w-7 h-7 flex items-center justify-center rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors cursor-pointer"
              >
                <i className="ri-subtract-line text-zinc-400 text-sm" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-full text-xs leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-red-600 text-white rounded-br-sm'
                      : 'bg-zinc-800 text-zinc-200 rounded-bl-sm'
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {thinking && (
              <div className="flex justify-start">
                <div className="bg-zinc-800 rounded-xl rounded-bl-sm px-3 py-2 flex gap-1 items-center">
                  {[0, 1, 2].map((d) => (
                    <span
                      key={d}
                      className="w-1.5 h-1.5 bg-zinc-500 rounded-full animate-bounce"
                      style={{ animationDelay: `${d * 0.15}s` }}
                    />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick suggestions */}
          <div className="px-3 pb-2 flex gap-1.5 overflow-x-auto scrollbar-none flex-shrink-0">
            {['Precios GPU', 'Horarios', '¿Dónde están?', 'Agendar'].map((s) => (
              <button
                key={s}
                onClick={() => sendMessage(s)}
                className="flex-shrink-0 text-[9px] font-bold text-zinc-400 border border-zinc-700 hover:border-red-600/50 hover:text-red-400 px-2 py-1 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                {s}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="px-3 pb-3 flex-shrink-0">
            <div className="flex gap-2 bg-zinc-900 border border-zinc-700 rounded-xl overflow-hidden">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Escribe tu pregunta..."
                className="flex-1 bg-transparent px-3 py-2.5 text-xs text-white placeholder-zinc-600 focus:outline-none"
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || thinking}
                className="px-3 text-red-500 hover:text-red-400 disabled:text-zinc-700 transition-colors cursor-pointer"
              >
                <i className="ri-send-plane-fill text-sm" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bot button + dismiss X */}
      <div
        className="relative flex items-end gap-2"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {/* Dismiss X — appears on hover */}
        <button
          onClick={handleDismiss}
          title={`Cerrar por ${DISMISS_MINUTES} min`}
          className={`w-6 h-6 flex items-center justify-center rounded-full bg-zinc-800 border border-zinc-700 hover:bg-red-600 hover:border-red-600 transition-all duration-200 cursor-pointer self-start mt-1 ${
            hover ? 'opacity-100 scale-100' : 'opacity-0 scale-75 pointer-events-none'
          }`}
        >
          <i className="ri-close-line text-zinc-300 text-xs" />
        </button>

        {/* Main bot button */}
        <button
          className="mf relative cursor-pointer"
          onClick={() => setOpen(!open)}
          title="Chatbot SpartechRD"
        >
          <div
            className={`absolute inset-0 rounded-2xl transition-all duration-300 ${
              hover
                ? 'shadow-[0_0_30px_8px_rgba(228,27,29,.5)]'
                : 'shadow-[0_0_15px_4px_rgba(228,27,29,.2)]'
            }`}
          />
          <div className="relative w-16 h-16 bg-zinc-950 border-2 border-red-600 rounded-2xl flex flex-col items-center justify-center gap-1 overflow-visible">
            <div className="absolute -top-3 w-1.5 h-4 bg-zinc-700 rounded-full">
              <div className="ap absolute -top-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-red-500 rounded-full" />
            </div>
            <div className="flex gap-2 mt-1">
              <div className="eb w-2.5 h-2.5 bg-red-500 rounded-full shadow-[0_0_6px_2px_rgba(228,27,29,.8)]" />
              <div
                className="eb w-2.5 h-2.5 bg-red-500 rounded-full shadow-[0_0_6px_2px_rgba(228,27,29,.8)]"
                style={{ animationDelay: '.1s' }}
              />
            </div>
            <div className="w-8 h-1.5 bg-red-600 rounded-full opacity-80" />
            <span className="text-[8px] text-zinc-500 font-bold uppercase tracking-widest mt-0.5">AI</span>
          </div>
          {/* Notification badge when closed */}
          {!open && (
            <div className="absolute -bottom-1 -right-1 flex items-center gap-1 bg-zinc-900 border border-zinc-700 rounded-full px-1.5 py-0.5">
              <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-[8px] text-emerald-700 font-bold">ON</span>
            </div>
          )}
        </button>
      </div>
    </div>
  );
}

